import { Link } from "react-router-dom";
import Page from "@/components/layout/Page";
import dementiaGears from "@/assets/mental-health/dementia-gears.png";
import nhsBadge from "@/assets/nhs-mental-health-badge.png";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Dementia() {
  return (
    <>
      <SEOHead
        title="Dementia Care | Tactile Sensory Tools for Memory Care | Samboards"
        description="Handmade tactile sensory tools for dementia and Alzheimer's care. Proven to reduce agitation, improve engagement & motor skills. NHS care homes trust. UK made."
        canonical="https://samboards.co.uk/mental-health/dementia"
        keywords="dementia sensory tools, Alzheimer's care tools, dementia tactile boards, memory care tools, care home sensory aids, dementia agitation relief, NHS dementia support, UK handmade"
        ogImage={dementiaGears}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Mental Health", url: "/mental-health/dementia" },
          { name: "Dementia", url: "/mental-health/dementia" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <Page>
        <section className="w-full pb-16 sm:pb-20 md:pb-24">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
            <h1 className="text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.1] font-bold page-title-gradient flex-1">Dementia</h1>
            <a 
              href="https://www.nhs.uk/mental-health/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 transition-all duration-200 hover:scale-105 hover:opacity-90 touch-manipulation"
              aria-label="NHS Mental Health Support"
            >
              <img 
                src={nhsBadge} 
                alt="NHS - Here to help with mental health" 
                className="h-12 sm:h-16 md:h-20 w-auto object-contain"
              />
            </a>
          </div>
          
          <div className="mental-health-hero-image">
            <img 
              src={dementiaGears} 
              alt="Dementia and cognitive function illustrated with brain gears"
            />
          </div>
          
          <div className="space-y-6 max-w-3xl">
            <p className="text-xl text-muted-foreground leading-relaxed">
              Sensory engagement tools to support individuals living with dementia and their carers.
            </p>
            
            <p className="text-lg leading-relaxed">
              Samboards sensory products can help individuals with dementia by providing calming tactile stimulation that reduces anxiety and feelings of overwhelm. The simple act of touching and feeling the different textures exercises the mind while providing a soothing, familiar activity.
            </p>
            
            <p className="text-lg leading-relaxed">
              For people living with dementia, sensory boards offer a non-threatening way to engage with their environment. The boards provide distraction during moments of confusion or agitation, and can be a source of enjoyment and comfort throughout the day.
            </p>
            
            <p className="text-lg leading-relaxed">
              Our products are also valuable tools for carers, offering a positive activity to share with their loved ones. The tactile nature of the boards can help facilitate moments of connection and calm, while giving carers a practical tool to help manage challenging situations.
            </p>
            
            <p className="text-lg leading-relaxed">
              Whether in care homes, day centres, or at home, Samboards provide meaningful sensory engagement that supports wellbeing and quality of life for individuals living with dementia.
            </p>
          </div>

          {/* Related Resources Section */}
          <div className="mt-16 pt-12 border-t border-slate-700/40">
            <h2 className="text-3xl font-semibold mb-3 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Recommended Products for Dementia Care
            </h2>
            <p className="text-slate-300/80 mb-8 max-w-2xl">
              These sensory tools provide meaningful engagement and calming support for individuals living with dementia.
            </p>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/samboards"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Samboards</h3>
                <p className="text-sm text-slate-200/90 mb-4">Full-size boards offering familiar tactile engagement and comfort.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/sensory-pads"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Sensory Pads</h3>
                <p className="text-sm text-slate-200/90 mb-4">Gentle tactile stimulation to reduce anxiety and provide soothing activity.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/library-squares"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Library Squares</h3>
                <p className="text-sm text-slate-200/90 mb-4">Quiet, non-threatening sensory engagement for care settings.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </Page>
    </div>
    </>
  );
}
